
import React, { useState, useEffect } from 'react';
import { Star, Circle, Calendar, Zap, Box, Sparkles, Crown, CheckCircle, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { TIER_CONFIG } from '../../constants.tsx';
import { createCheckoutSession } from '../../services/stripeService.ts';
import { trackEvent } from '../../lib/analytics.ts';

interface PricingCardProps {
  tierKey: string;
  userTier: string;
  userId: string;
  userEmail?: string;
}

const TIER_WEIGHTS: Record<string, number> = {
  'registered': 0,
  'plus': 1,
  'pro': 2,
  'god': 3
};

const PricingCard: React.FC<PricingCardProps> = ({ tierKey, userTier, userId, userEmail }) => {
  const tier = TIER_CONFIG[tierKey as keyof typeof TIER_CONFIG];
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayPrice, setDisplayPrice] = useState(tier.priceLabel);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setDisplayPrice(tier.priceLabel);
      setIsAnimating(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [tier.priceLabel]);

  const getCardStyle = () => {
    switch (tierKey) {
      case 'registered': return 'pricing-card-zinc';
      case 'plus': return 'pricing-card-blue';
      case 'pro': return 'pricing-card-purple';
      case 'god': return 'pricing-card-yellow animate-blast-glow';
      default: return 'pricing-card-zinc';
    }
  };

  const getIcon = () => {
    switch (tierKey) {
      case 'registered': return <Circle size={16} />;
      case 'plus': return <Star size={16} />;
      case 'pro': return <Sparkles size={16} />;
      case 'god': return <Crown size={16} />;
      default: return <Circle size={16} />;
    }
  };

  const userWeight = TIER_WEIGHTS[userTier] ?? 0;
  const cardWeight = TIER_WEIGHTS[tierKey] ?? 0;
  const isCurrent = userTier === tierKey;
  const isUpgrade = cardWeight > userWeight;
  const isDowngrade = cardWeight < userWeight;

  const getButtonLabel = () => {
    if (isCurrent) return 'CURRENT RANK';
    if (isUpgrade) return 'UPGRADE RANK';
    if (isDowngrade) return 'DOWNGRADE RANK';
    return 'SECURE RANK';
  };

  const getButtonIcon = () => {
    if (isCurrent) return <CheckCircle size={16} />;
    if (isUpgrade) return <ArrowUpCircle size={16} />;
    if (isDowngrade) return <ArrowDownCircle size={16} />;
    return <Calendar size={16} />;
  };

  const blastsPerMonth = tier.dailyLimit * 30;

  return (
    <div className={`pricing-card ${getCardStyle()} ${isCurrent ? 'border-2 border-yellow-400 scale-105 z-10' : ''}`}>
      <div className="bg"></div>
      <div className="wrap">
        <div className="outline"></div>
        <div className="content p-8 flex flex-col text-white h-full">
          <div className="text-[10px] font-black bg-white/10 rounded-full px-4 py-1.5 inline-flex items-center gap-2 self-center uppercase tracking-widest border border-white/5">
            {getIcon()}
            {tier.label}
          </div>
          
          <div className="flex flex-col items-center my-6">
            <p className="text-6xl lg:text-7xl font-black">{displayPrice}</p>
            <p className="text-[9px] font-black text-white/40 tracking-[0.3em] uppercase mt-2">
              {tier.priceLabel === 'Free' ? 'FOREVER' : 'PER MONTH'}
            </p>
          </div>
          
          <div className={`card-details transition-all duration-300 flex-1 flex flex-col ${isAnimating ? 'details-hidden' : ''}`}>
            <p className="text-white/50 mb-8 text-center text-[10px] font-medium leading-relaxed uppercase tracking-tighter">
              Level up your brainrot frequency with {tier.label} rank features.
            </p>
            
            <div className="flex flex-wrap justify-center gap-2 mb-8 flex-1">
              <span className="feature-tag flex items-center gap-1"><Zap size={10}/> {tier.dailyLimit} Blasts ({blastsPerMonth}/mo)</span>
              <span className="feature-tag flex items-center gap-1"><Box size={10}/> {tier.vaultLimit === 'unlimited' ? '∞' : tier.vaultLimit} Slots</span>
              <span className="feature-tag flex items-center gap-1"><Sparkles size={10}/> +{Math.round(tier.xpBonus * 100)}% Aura</span>
            </div>

            <button 
              onClick={() => {
                trackEvent('initiate_checkout', {
                  tier: tierKey,
                  price: tier.priceLabel,
                  user_tier: userTier
                });
                !isCurrent && createCheckoutSession(tierKey, userId, userEmail);
              }}
              disabled={isCurrent}
              className={`mt-auto w-full py-4 rounded-2xl font-bungee text-sm transition-all flex items-center justify-center gap-2 shadow-lg ${
                isCurrent 
                ? 'bg-zinc-800 text-zinc-500 cursor-default border border-zinc-700' 
                : isUpgrade 
                  ? 'bg-indigo-600 text-white hover:bg-indigo-500 hover:scale-105 active:scale-95' 
                  : 'bg-zinc-700 text-white hover:bg-zinc-600 hover:scale-105 active:scale-95'
              }`}
            >
              {getButtonIcon()}
              {getButtonLabel()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AnimatedPricingPage: React.FC<{ user: any, userTier: string }> = ({ user, userTier }) => {
  return (
    <div className="relative w-full flex flex-col items-center">
      <div className="shape-1"></div>
      <div className="shape-2"></div>
      
      <div className="relative z-10 flex flex-col items-center w-full max-w-7xl pt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full px-4">
          <PricingCard tierKey="registered" userTier={userTier} userId={user?.id} userEmail={user?.email} />
          <PricingCard tierKey="plus" userTier={userTier} userId={user?.id} userEmail={user?.email} />
          <PricingCard tierKey="pro" userTier={userTier} userId={user?.id} userEmail={user?.email} />
          <PricingCard tierKey="god" userTier={userTier} userId={user?.id} userEmail={user?.email} />
        </div>
      </div>
    </div>
  );
};
